import type { StateNodeConfig } from '@people-science/state-machine';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';

import { getDefaultFormElementPlugins } from '../plugins/form-elements/default';
import { isResumableStatePath } from './resumableStatePath';
import {
  clearFormDraft,
  createDebouncedDraftWriter,
  DebouncedDraftWriter,
  formDraftKey,
  FormDraftPayload,
  loadFormDraft
} from './formDraftStore';

const WRITE_DEBOUNCE_MS = 750;

export interface UseFormDraftParams {
  userId?: number | string;
  formTemplateId?: number | string;
  /** entityDataId or participantEventLogId; absent until the first save */
  instanceId?: number | string;
  formTemplateVersion?: number;
  /** template state chart; a draft only resumes at a section it can sit on */
  formConfig?: StateNodeConfig;
}

export interface FormDraftApi {
  /** false on web (worse at-rest story) or while identity is unknown */
  enabled: boolean;
  restoreDraft: () => Promise<FormDraftPayload | undefined>;
  /** debounced write on field changes */
  saveDraftValues: (formValues: any) => void;
  /** immediate write-through on a section transition */
  saveDraftStatePath: (statePath: string) => void;
  /** drop pending writes and remove the stored draft (form completed) */
  clearDraft: () => void;
}

/**
 * Write-through local draft for a dynamic form instance (CD-222). Mobile-only:
 * web drafts would land in browser localStorage, a worse at-rest story on
 * shared computers.
 *
 * The draft key is stable per user+template; the instance identity rides in
 * the payload and is matched on restore, so the first save assigning an
 * entity id mid-flow needs no key migration.
 */
export const useFormDraft = ({
  userId,
  formTemplateId,
  instanceId,
  formTemplateVersion,
  formConfig
}: UseFormDraftParams): FormDraftApi => {
  const enabled =
    Platform.OS !== 'web' && userId != null && formTemplateId != null;
  const draftKey = enabled
    ? formDraftKey({ userId: userId!, formTemplateId: formTemplateId! })
    : undefined;

  // @types/react 19 requires an explicit initial value for useRef
  const writerRef = useRef<
    { key: string; writer: DebouncedDraftWriter } | undefined
  >(undefined);
  const versionRef = useRef(formTemplateVersion);
  versionRef.current = formTemplateVersion;
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;
  const formConfigRef = useRef(formConfig);
  formConfigRef.current = formConfig;
  const canResumeAt = useCallback(
    (statePath: string) =>
      isResumableStatePath(
        formConfigRef.current,
        statePath,
        getDefaultFormElementPlugins()
      ),
    []
  );

  const getWriter = useCallback((): DebouncedDraftWriter | undefined => {
    if (!draftKey) return undefined;
    if (writerRef.current?.key !== draftKey) {
      writerRef.current?.writer.cancel();
      writerRef.current = {
        key: draftKey,
        writer: createDebouncedDraftWriter(draftKey, WRITE_DEBOUNCE_MS, {
          canResume: canResumeAt
        })
      };
    }
    return writerRef.current.writer;
  }, [draftKey, canResumeAt]);

  // persist any pending keystrokes when the screen unmounts (backing out
  // mid-form keeps the draft; completion clears it via clearDraft first)
  useEffect(
    () => () => {
      writerRef.current?.writer.flush();
    },
    []
  );

  const restoreDraft = useCallback(async () => {
    if (!draftKey) return undefined;
    return loadFormDraft(draftKey, {
      formTemplateVersion: versionRef.current,
      instanceId: instanceIdRef.current,
      canResume: canResumeAt
    });
  }, [draftKey, canResumeAt]);

  const saveDraftValues = useCallback(
    (formValues: any) => {
      getWriter()?.write({
        formValues,
        formTemplateVersion: versionRef.current,
        instanceId: instanceIdRef.current
      });
    },
    [getWriter]
  );

  const saveDraftStatePath = useCallback(
    (statePath: string) => {
      const writer = getWriter();
      if (!writer) return;
      writer.write({
        statePath,
        formTemplateVersion: versionRef.current,
        instanceId: instanceIdRef.current
      });
      writer.flush();
    },
    [getWriter]
  );

  const clearDraft = useCallback(() => {
    writerRef.current?.writer.cancel();
    if (draftKey) clearFormDraft(draftKey);
  }, [draftKey]);

  return useMemo(
    () => ({
      enabled,
      restoreDraft,
      saveDraftValues,
      saveDraftStatePath,
      clearDraft
    }),
    [enabled, restoreDraft, saveDraftValues, saveDraftStatePath, clearDraft]
  );
};
